// Guided tour: walks a person through the app one screen at a time. Each step can
// open a route, spotlight an element (a CSS selector, first visible match) and
// explain it in a small card. Steps whose permission the person lacks are skipped;
// a target that never appears (narrow screen, empty page) falls back to a centred card.
import type { Permission } from '@zenarty/shared';
import { ArrowLeft, ArrowRight, Compass, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../auth';
import { Button } from './kit';

export interface TourStep {
  id: string;
  /** Route to open before showing the step. */
  route?: string;
  /** CSS selector of the element to spotlight; omit for a centred card. */
  target?: string;
  title: string;
  body: ReactNode;
  placement?: 'right' | 'left' | 'top' | 'bottom';
  permission?: Permission;
}

interface TourApi {
  start: () => void;
  running: boolean;
}

const TourContext = createContext<TourApi>({ start: () => undefined, running: false });
export const useTour = () => useContext(TourContext);

const doneKey = (userId: string) => `wms.tour.done.${userId}`;

export function tourDone(userId: string): boolean {
  try {
    return localStorage.getItem(doneKey(userId)) === '1';
  } catch {
    return true; // no storage: never auto-start (it could not remember being dismissed)
  }
}

function markDone(userId: string) {
  try {
    localStorage.setItem(doneKey(userId), '1');
  } catch {
    /* storage unavailable */
  }
}

function findTarget(selector: string): Element | null {
  for (const el of Array.from(document.querySelectorAll(selector))) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

export function TourProvider({ steps, children }: { steps: TourStep[]; children: ReactNode }) {
  const { me, can } = useAuth();
  const [running, setRunning] = useState(false);
  const visible = useMemo(
    () => steps.filter((s) => !s.permission || can(s.permission)),
    [steps, can],
  );
  const start = useCallback(() => setRunning(true), []);
  const stop = useCallback(() => {
    setRunning(false);
    if (me) markDone(me.id);
  }, [me]);
  const api = useMemo(() => ({ start, running }), [start, running]);
  return (
    <TourContext.Provider value={api}>
      {children}
      {running && visible.length ? <TourOverlay steps={visible} onClose={stop} /> : null}
    </TourContext.Provider>
  );
}

const PAD = 8;
const CARD_W = 360;
const GAP = 14;

function TourOverlay({ steps, onClose }: { steps: TourStep[]; onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [ready, setReady] = useState(false);
  const [card, setCard] = useState({ w: CARD_W, h: 220 });
  const cardRef = useRef<HTMLDivElement>(null);
  const step = steps[index]!;
  const last = index === steps.length - 1;

  // Open the step's route, then wait (up to 3 s) for its target to render.
  useEffect(() => {
    setReady(false);
    setRect(null);
    if (step.route && location.pathname !== step.route) {
      void navigate(step.route);
      return;
    }
    if (!step.target) {
      setReady(true);
      return;
    }
    let tries = 0;
    let timer = 0;
    const look = () => {
      const el = findTarget(step.target!);
      if (el) {
        // Scroll only when the element is off screen; a tall one shows from its top.
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.top < 64 || r.bottom > vh - 8)
          el.scrollIntoView({ block: r.height > vh * 0.7 ? 'start' : 'center', inline: 'nearest' });
        timer = window.setTimeout(() => {
          setRect(el.getBoundingClientRect());
          setReady(true);
        }, 120);
      } else if (++tries < 30) {
        timer = window.setTimeout(look, 100);
      } else {
        setReady(true); // not on screen: show the card centred
      }
    };
    look();
    return () => window.clearTimeout(timer);
  }, [step, location.pathname, navigate]);

  // Keep the spotlight on the element while the page scrolls or resizes.
  useEffect(() => {
    if (!ready || !step.target) return;
    const update = () => {
      const el = findTarget(step.target!);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    const t = window.setInterval(update, 500); // data loading can move things
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.clearInterval(t);
    };
  }, [ready, step]);

  useLayoutEffect(() => {
    if (!ready || !cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    setCard((c) => (c.w === r.width && c.h === r.height ? c : { w: r.width, h: r.height }));
    cardRef.current.querySelector<HTMLButtonElement>('.tour-foot .btn-primary')?.focus({
      preventScroll: true,
    });
  }, [ready, index]);

  const go = useCallback(
    (to: number) => {
      if (to < 0) return;
      if (to >= steps.length) {
        onClose();
        return;
      }
      setIndex(to);
    },
    [steps.length, onClose],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(index + 1);
      else if (e.key === 'ArrowLeft') go(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, index, onClose]);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const narrow = vw < 640;
  let pos: { left: number; top: number };
  // The spotlight stays inside the window so its outline is never cut off.
  const hole = rect
    ? (() => {
        const left = Math.max(4, rect.left - PAD);
        const top = Math.max(4, rect.top - PAD);
        const right = Math.min(vw - 4, rect.right + PAD);
        const bottom = Math.min(vh - 4, rect.bottom + PAD);
        return { left, top, width: right - left, height: bottom - top };
      })()
    : null;
  if (!hole || narrow) {
    pos =
      hole && narrow
        ? {
            left: 12,
            top: hole.top + hole.height + GAP < vh - card.h ? hole.top + hole.height + GAP : 12,
          }
        : { left: (vw - card.w) / 2, top: Math.max(24, (vh - card.h) / 2) };
  } else {
    const fits = {
      right: hole.left + hole.width + GAP + card.w < vw - 12,
      left: hole.left - GAP - card.w > 12,
      bottom: hole.top + hole.height + GAP + card.h < vh - 12,
      top: hole.top - GAP - card.h > 12,
    };
    const order = [step.placement ?? 'bottom', 'bottom', 'right', 'top', 'left'] as const;
    const side = order.find((p) => fits[p]) ?? 'bottom';
    const cx = hole.left + hole.width / 2 - card.w / 2;
    const cy = hole.top + hole.height / 2 - card.h / 2;
    pos =
      side === 'right'
        ? { left: hole.left + hole.width + GAP, top: cy }
        : side === 'left'
          ? { left: hole.left - GAP - card.w, top: cy }
          : side === 'top'
            ? { left: cx, top: hole.top - GAP - card.h }
            : { left: cx, top: hole.top + hole.height + GAP };
    pos.left = Math.min(Math.max(12, pos.left), vw - card.w - 12);
    pos.top = Math.min(Math.max(12, pos.top), vh - card.h - 12);
  }

  return createPortal(
    <div className="tour-layer">
      {hole ? <div className="tour-hole" style={hole} /> : <div className="tour-dim" />}
      <div
        ref={cardRef}
        className="tour-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        style={{
          left: pos.left,
          top: pos.top,
          width: narrow ? vw - 24 : CARD_W,
          opacity: ready ? 1 : 0,
        }}
      >
        <div className="tour-head">
          <span className="tour-step">
            <Compass aria-hidden /> {index + 1} of {steps.length}
          </span>
          <Button variant="ghost" size="sm" icon={X} aria-label="Close tour" onClick={onClose} />
        </div>
        <h2 id="tour-title">{step.title}</h2>
        <div className="tour-body">{step.body}</div>
        <div className="tour-progress" aria-hidden>
          <span style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
        </div>
        <div className="tour-foot">
          {index === 0 ? (
            <Button variant="ghost" size="sm" onClick={onClose}>
              Skip tour
            </Button>
          ) : (
            <Button size="sm" icon={ArrowLeft} onClick={() => go(index - 1)}>
              Back
            </Button>
          )}
          <span className="spacer" />
          <Button variant="primary" size="sm" onClick={() => go(index + 1)}>
            {last ? 'Finish' : index === 0 ? 'Start the tour' : 'Next'}
            {last ? null : <ArrowRight aria-hidden />}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
